/**
 * Service for fetching point data from the API
 * Handles zoom-based data loading with H3 aggregation
 */

import { H3_CONFIG } from '../workers/h3Config';
import type { FieldSchema } from '../types/metadata';
import { getAuthHeaders } from '../utils/authHeaders';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

export interface DataPoint {
  coords: [number, number];
  weight: number;
  holes: number[][];
  id?: string | number; // Unique identifier for the record
  fields?: Record<string, any>; // Extra field data (e.g., for color code joins)
}

export interface AggregatedDataPoint {
  h3Index: string;
  recordCount: number;
  coords: [number, number][];
}

export interface DataResponse {
  points: DataPoint[];
  aggregations?: AggregatedDataPoint[];
  totalCount: number;
  mode: 'points' | 'heatmap';
  resolution?: number;
}

interface Viewport {
  latitude: number;
  longitude: number;
  zoom: number;
  bounds?: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
}

// Cache for H3 cells per file
const h3CellsCache = new Map<string, Set<string>>();

// Cache for per-hex point counts: Map<"fileId-version-h3Cell", number>
// Once a hex count is cached, it doesn't need to be re-fetched unless the file version changes
const h3CellCountsCache = new Map<string, number>();

// Track last requested H3 cells for debugging
let lastRequestedH3Cells: string[] = [];

const log = {
  info: (message: string, data?: any) => {
    console.log(`[DataService] ${message}`, data || '');
  },
  warn: (message: string, data?: any) => {
    console.warn(`[DataService] ⚠️ ${message}`, data || '');
  },
  success: (message: string, data?: any) => {
    console.log(`[DataService] ✅ ${message}`, data || '');
  },
  error: (message: string, data?: any) => {
    console.error(`[DataService] ❌ ${message}`, data || '');
  },
};

/**
 * Client-side filter matching for a single row value against an API filter string.
 * Supports formats: "value" (equals), ">30", "<10", ">=5", "<=100", "!=foo", "IN val1,val2"
 * String comparisons are case-insensitive.
 */
const matchesApiFilter = (rowValue: any, filterStr: string): boolean => {
  if (rowValue === null || rowValue === undefined) {
    return filterStr.startsWith('!=');
  }

  // Parse operator from filter string
  if (filterStr.startsWith('IN ')) {
    const values = filterStr.slice(3).split(',').map(v => v.trim().toLowerCase());
    return values.includes(String(rowValue).toLowerCase());
  }
  if (filterStr.startsWith('!=')) {
    return String(rowValue).toLowerCase() !== filterStr.slice(2).trim().toLowerCase();
  }
  if (filterStr.startsWith('>=')) {
    const num = Number(filterStr.slice(2));
    return !isNaN(num) ? Number(rowValue) >= num : String(rowValue).toLowerCase() >= filterStr.slice(2).toLowerCase();
  }
  if (filterStr.startsWith('<=')) {
    const num = Number(filterStr.slice(2));
    return !isNaN(num) ? Number(rowValue) <= num : String(rowValue).toLowerCase() <= filterStr.slice(2).toLowerCase();
  }
  if (filterStr.startsWith('>')) {
    const num = Number(filterStr.slice(1));
    return !isNaN(num) ? Number(rowValue) > num : String(rowValue).toLowerCase() > filterStr.slice(1).toLowerCase();
  }
  if (filterStr.startsWith('<')) {
    const num = Number(filterStr.slice(1));
    return !isNaN(num) ? Number(rowValue) < num : String(rowValue).toLowerCase() < filterStr.slice(1).toLowerCase();
  }
  // Default: equals (case-insensitive)
  return String(rowValue).toLowerCase() === filterStr.trim().toLowerCase();
};

/**
 * Get the last requested H3 cells (for debugging)
 */
export const getLastRequestedH3Cells = (): string[] => {
  return lastRequestedH3Cells;
};

/**
 * Get the cached point count for a specific H3 res-2 cell.
 * Returns undefined if not cached yet.
 */
const getCachedCellCount = (fileId: string, version: number, h3Cell: string): number | undefined => {
  const cacheKey = `${fileId}-${version}-${h3Cell}`;
  return h3CellCountsCache.get(cacheKey);
};

/**
 * Set the cached point count for an H3 res-2 cell.
 */
const setCachedCellCount = (fileId: string, version: number, h3Cell: string, count: number): void => {
  const cacheKey = `${fileId}-${version}-${h3Cell}`;
  h3CellCountsCache.set(cacheKey, count);
};

/**
 * Fetch the point count for a single H3 res-2 cell from the backend.
 * When viewportBounds are provided, returns the count within those bounds (not cached,
 * since the viewport changes on each pan). Without bounds, returns the total cell count (cached).
 * When filters are provided, the count reflects only matching records (not cached).
 */
const fetchCellPointCount = async (
  fileId: string,
  version: number,
  h3Cell: string,
  viewportBounds?: { north: number; south: number; east: number; west: number },
  filters?: Record<string, string | string[]>
): Promise<number> => {
  const hasFilters = filters && Object.keys(filters).length > 0;

  // Only use cache for non-viewport-bounded, non-filtered counts (total cell counts)
  if (!viewportBounds && !hasFilters) {
    const cached = getCachedCellCount(fileId, version, h3Cell);
    if (cached !== undefined) {
      return cached;
    }
  }

  try {
    const url = `${API_BASE_URL}/retrieve`;
    const requestBody: any = {
      h3: h3Cell,
      fileId,
      version,
      fields: ['latitude'], // Minimal field to get totalMatches count
      limit: 1, // We only need the count, not the data
    };
    
    // Include viewport bounds for accurate in-view count
    if (viewportBounds) {
      requestBody.viewportBounds = viewportBounds;
    }

    // Include filters so count reflects only matching records
    if (hasFilters) {
      requestBody.filters = filters;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      log.warn(`Failed to fetch count for cell ${h3Cell}: ${response.status}`);
      return 0;
    }

    const data = await response.json();
    const count = data.totalMatches || 0;

    // Only cache non-viewport-bounded, non-filtered counts (total cell count is stable)
    if (!viewportBounds && !hasFilters) {
      setCachedCellCount(fileId, version, h3Cell, count);
      log.info(`Cached total count for cell ${h3Cell}: ${count}`);
    } else {
      log.info(`Viewport/filtered count for cell ${h3Cell}: ${count}`);
    }

    return count;
  } catch (error) {
    log.warn(`Failed to fetch count for cell ${h3Cell}`, error);
    return 0;
  }
};

/**
 * Estimate the total point count within the viewport by summing cached H3 cell counts.
 * For cells not yet cached, fetches them from the backend.
 * Returns the total estimated points in view.
 * When filters are provided, counts reflect only matching records.
 */
const estimateViewportPointCount = async (
  fileId: string,
  version: number,
  viewport: Viewport,
  filters?: Record<string, string | string[]>
): Promise<{ totalInView: number; cellCounts: Map<string, number> }> => {
  const baseResolution = H3_CONFIG.BASE_RESOLUTION;
  const h3BaseCells = await getH3CellsForViewport(viewport, baseResolution);
  const cellsWithData = await getH3CellsWithData(fileId, version);

  // Filter to only cells that actually have data
  const cellsToCheck = cellsWithData.size > 0
    ? h3BaseCells.filter(cell => cellsWithData.has(cell))
    : h3BaseCells;

  // Fetch viewport-aware counts for all cells
  // Uses viewportBounds to get an accurate count of points actually in view,
  // not just the total for the entire res-2 cell parquet file.
  // When filters are active, counts reflect only matching records.
  const cellCounts = new Map<string, number>();
  const fetchPromises = cellsToCheck.map(async (cell) => {
    const count = await fetchCellPointCount(fileId, version, cell, viewport.bounds, filters);
    cellCounts.set(cell, count);
  });

  await Promise.all(fetchPromises);

  const totalInView = Array.from(cellCounts.values()).reduce((sum, count) => sum + count, 0);

  log.info('Viewport point count estimated', {
    fileId,
    cellsInView: cellsToCheck.length,
    totalInView,
    cellCounts: Object.fromEntries(cellCounts),
    hasViewportBounds: !!viewport.bounds,
    hasFilters: !!(filters && Object.keys(filters).length > 0),
  });

  return { totalInView, cellCounts };
};

/**
 * Get H3 cells that have data for a file (from metadata)
 */
const getH3CellsWithData = async (fileId: string, version: number): Promise<Set<string>> => {
  const cacheKey = `${fileId}-${version}`;
  
  // Check cache first
  if (h3CellsCache.has(cacheKey)) {
    return h3CellsCache.get(cacheKey)!;
  }
  
  try {
    const response = await fetch(`${API_BASE_URL}/metadata?id=${fileId}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch metadata');
    }
    
    const data = await response.json();
    const fileMetadata = data.files.find((f: any) => 
      f.id === fileId && f.version === version
    );
    
    const h3Cells = fileMetadata?.h3_cells || [];
    const cellsSet = new Set<string>(h3Cells);
    
    // Cache the result
    h3CellsCache.set(cacheKey, cellsSet);
    
    log.info(`Loaded H3 cells for file ${fileId}`, { count: cellsSet.size });
    
    return cellsSet;
  } catch (error) {
    log.error('Failed to get H3 cells from metadata', error);
    // Return empty set on error - will try all cells
    return new Set();
  }
};

/**
 * Get total point count for a file
 */
export const getPointCount = async (fileId: string, version: number): Promise<number> => {
  try {
    const response = await fetch(`${API_BASE_URL}/metadata?id=${fileId}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch metadata');
    }
    
    const data = await response.json();
    const fileMetadata = data.files.find((f: any) => 
      f.id === fileId && f.version === version
    );
    
    const pointsCount = fileMetadata?.count;
    
    // Require count to be present and non-zero
    if (!pointsCount || pointsCount === 0) {
      throw new Error(
        `FATAL: count is ${pointsCount === 0 ? 'zero' : 'missing'} in metadata for file ${fileId}. ` +
        'The backend MUST populate this field during file processing. ' +
        'Please reprocess this file or check the process-file Lambda function.'
      );
    }
    
    return pointsCount;
  } catch (error) {
    log.error('Failed to get point count', error);
    throw error;
  }
};

/**
 * Load all data for a dataset
 */
export const loadAllData = async (
  fileId: string,
  version: number,
  fields?: string[]
): Promise<DataResponse> => {
  const startTime = performance.now();
  
  try {
    log.info('Loading all data', { fileId, version, fields });
    
    const fieldsParam = fields ? `&fields=${fields.join(',')}` : '';
    const url = `${API_BASE_URL}/retrieve-all?fileId=${fileId}&version=${version}${fieldsParam}`;
    
    const response = await fetch(url, {
      headers: getAuthHeaders(),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      
      if (response.status === 400 && errorData.pointsCount) {
        // Dataset exceeds limit
        throw new Error(errorData.message || `Dataset has ${errorData.pointsCount} points, exceeds limit of ${errorData.maxAllowed}`);
      }
      
      throw new Error(errorData.error || 'Failed to fetch all data');
    }
    
    const data = await response.json();
    
    // Convert API response to DataPoint format
    const points: DataPoint[] = data.data.map((row: any) => ({
      coords: [row.longitude, row.latitude],
      weight: 1,
      holes: [],
      id: row.id,
    }));
    
    const duration = performance.now() - startTime;
    log.success('All data loaded', {
      count: points.length,
      duration: `${duration.toFixed(2)}ms`,
    });
    
    return {
      points,
      totalCount: data.totalCount,
      mode: 'points',
    };
  } catch (error) {
    log.error('Failed to load all data', error);
    throw error;
  }
};

/**
 * Load data based on zoom level and point count
 * - If total dataset < threshold: load all points directly
 * - If viewport H3 hex point estimate < threshold: load individual points for the view
 * - Otherwise: use zoom-based H3 aggregation (heatmap mode)
 * 
 * H3 hex point counts are cached per session, so the viewport estimation
 * only requires API calls for hexes not yet seen.
 */
export const loadLayerData = async (
  fileId: string,
  viewport: Viewport,
  version: number,
  pointsCount?: number, // Optional: pass from LayerConfig.count to avoid extra API call
  schema?: FieldSchema, // Optional: pass schema to determine ID field
  heatmapThreshold?: number, // Optional: threshold for heatmap vs points mode (default 500)
  filters?: Record<string, string | string[]>, // Optional: API-formatted filters from data table
  colorCodeFields?: string[] // Optional: extra fields to include on DataPoints for color code joins
): Promise<DataResponse> => {
  const startTime = performance.now();
  
  try {
    // Get total point count (use provided count or fetch from metadata)
    let totalCount: number;
    
    if (pointsCount !== undefined && pointsCount !== null) {
      // Use provided count from LayerConfig.count
      totalCount = pointsCount;
      
      // Fatal error if count is zero or invalid
      if (totalCount === 0) {
        throw new Error(
          `FATAL: count is ZERO for file ${fileId}. ` +
          'The backend MUST populate this field during file processing. ' +
          'Please reprocess this file or check the process-file Lambda function.'
        );
      }
    } else {
      // Fetch from metadata (will throw if missing/zero)
      totalCount = await getPointCount(fileId, version);
    }
    
    log.info('Loading layer data', {
      fileId,
      totalCount,
      zoom: viewport.zoom.toFixed(2),
      version,
    });
    
    // Use provided threshold or default to 500
    const threshold = heatmapThreshold ?? 500;
    
    // Estimate point count within the current viewport using cached H3 cell counts.
    // This gives us a viewport-aware decision instead of using the entire dataset size.
    const baseResolution = H3_CONFIG.BASE_RESOLUTION;
    const h3BaseCells = await getH3CellsForViewport(viewport, baseResolution);
    lastRequestedH3Cells = h3BaseCells;
    
    const hasFilters = filters && Object.keys(filters).length > 0;
    
    // If the total dataset is small AND no filters are active, skip viewport estimation
    // and load all points directly. When filters are active, totalCount reflects the
    // unfiltered dataset so we can't use it as a shortcut — we must estimate the
    // filtered viewport count instead.
    if (!hasFilters && totalCount < threshold) {
      log.info(`Total dataset below threshold (${totalCount} < ${threshold}), loading all points`);
      const points = await loadAllPoints(fileId, version, schema, filters, colorCodeFields);
      
      log.info(`Tracked ${h3BaseCells.length} res-${baseResolution} cells for viewport (all points mode)`);
      
      const duration = performance.now() - startTime;
      log.success('All points loaded', {
        count: points.length,
        duration: `${duration.toFixed(2)}ms`,
      });
      
      return {
        points,
        totalCount,
        mode: 'points',
      };
    }
    
    // For larger datasets (or when filters are active), estimate points in the viewport
    // using H3 hex counts — passing filters so the count reflects only matching records.
    const { totalInView } = await estimateViewportPointCount(fileId, version, viewport, filters);
    
    log.info('Viewport-based threshold check', {
      totalInView,
      threshold,
      totalDataset: totalCount,
      decision: totalInView < threshold ? 'points' : 'heatmap',
    });
    
    // If the points within the viewport are below the threshold, load individual points
    if (totalInView < threshold) {
      log.info(`Viewport points below threshold (${totalInView} < ${threshold}), loading points for view`);
      
      // Use the zoom-based settings to determine filtering
      const settings = H3_CONFIG.getSettingsForZoom(viewport.zoom);
      const points = await loadPointsInViewport(fileId, viewport, version, settings.filterRes, filters, colorCodeFields);
      
      const duration = performance.now() - startTime;
      log.success('Points loaded for viewport (threshold-based)', {
        count: points.length,
        estimatedInView: totalInView,
        threshold,
        filterRes: settings.filterRes,
        duration: `${duration.toFixed(2)}ms`,
      });
      
      return {
        points,
        totalCount,
        mode: 'points',
        resolution: settings.filterRes ?? undefined,
      };
    }
    
    // Otherwise, use zoom-based loading with new config
    const settings = H3_CONFIG.getSettingsForZoom(viewport.zoom);
    
    log.info('Using zoom-based loading', {
      zoom: viewport.zoom.toFixed(2),
      filterRes: settings.filterRes,
      aggRes: settings.aggRes,
      totalInView,
      threshold,
    });
    
    // Determine mode based on aggRes AND threshold
    if (settings.aggRes === null) {
      // Zoom config says "show raw points", but we must respect the heatmap threshold.
      // If estimated points in viewport exceed the threshold, use aggregation at the
      // finest resolution (filterRes) instead of raw points — otherwise we'd load
      // far too many points and get non-deterministic results from LIMIT-based queries.
      if (totalInView >= threshold) {
        log.info(`Viewport has ${totalInView} points >= threshold ${threshold} — using aggregation at res-${settings.filterRes} despite aggRes=null`);
        const aggResolution = settings.filterRes ?? 8;
        const aggregations = await loadAggregations(fileId, viewport, aggResolution, version, settings.filterRes, filters);
        
        const duration = performance.now() - startTime;
        log.success('Aggregations loaded (threshold override)', {
          count: aggregations.length,
          aggRes: aggResolution,
          totalInView,
          threshold,
          duration: `${duration.toFixed(2)}ms`,
        });
        
        return {
          points: [],
          aggregations,
          totalCount,
          mode: 'heatmap',
          resolution: aggResolution,
        };
      }
      
      // Below threshold: safe to load individual points
      const points = await loadPointsInViewport(fileId, viewport, version, settings.filterRes, filters, colorCodeFields);
      
      const duration = performance.now() - startTime;
      log.success('Points loaded for viewport', {
        count: points.length,
        filterRes: settings.filterRes,
        duration: `${duration.toFixed(2)}ms`,
      });
      
      return {
        points,
        totalCount,
        mode: 'points',
        resolution: settings.filterRes ?? undefined,
      };
    } else {
      // Aggregation mode: load aggregations at aggRes
      const aggregations = await loadAggregations(fileId, viewport, settings.aggRes, version, settings.filterRes, filters);
      
      const duration = performance.now() - startTime;
      log.success('Aggregations loaded', {
        count: aggregations.length,
        filterRes: settings.filterRes,
        aggRes: settings.aggRes,
        duration: `${duration.toFixed(2)}ms`,
      });
      
      return {
        points: [],
        aggregations,
        totalCount,
        mode: 'heatmap',
        resolution: settings.aggRes,
      };
    }
  } catch (error) {
    log.error('Failed to load layer data', error);
    throw error;
  }
};

/**
 * Load all points for a file (used when below heatmap threshold)
 */
const loadAllPoints = async (fileId: string, version: number, schema?: FieldSchema, filters?: Record<string, string | string[]>, colorCodeFields?: string[]): Promise<DataPoint[]> => {
  // Don't specify fields - let backend return all fields including the ID field
  const url = `${API_BASE_URL}/retrieve-all?fileId=${fileId}&version=${version}`;
  
  // Determine which field to use as ID from schema
  const { getIdField } = await import('../types/metadata');
  const idField = schema ? getIdField(schema) : null;
  
  log.info('Loading all points via /retrieve-all', { fileId, version, url, idField });
  
  try {
    const response = await fetch(url, {
      headers: getAuthHeaders(),
    });
    
    if (!response.ok) {
      // Read the error response body once
      const errorText = await response.text();
      
      // If 404, the endpoint might not be deployed yet
      if (response.status === 404) {
        log.warn('/retrieve-all endpoint not found (404)', { error: errorText });
        throw new Error('ENDPOINT_NOT_FOUND');
      }
      
      // If 400, check for specific error messages
      if (response.status === 400) {
        try {
          const errorJson = JSON.parse(errorText);
          if (errorJson.error && errorJson.error.includes('H3 cells')) {
            log.warn('H3 cells not found in metadata - backend needs to populate h3_cells field');
            throw new Error('H3_CELLS_NOT_FOUND');
          }
        } catch (parseError) {
          // If we can't parse the error, continue with generic error
        }
      }
      
      log.error('Failed to fetch all data', { 
        status: response.status, 
        statusText: response.statusText,
        error: errorText 
      });
      throw new Error(`Failed to fetch all data: ${response.status} ${response.statusText}`);
    }
    
    // Read response body only once
    const data = await response.json();
    
    // Convert API response to DataPoint format
    const allPoints: DataPoint[] = [];
    const rawRows = data.data;
    
    // If filters are applied, filter the raw rows first
    let filteredRows = rawRows;
    if (filters && Object.keys(filters).length > 0) {
      filteredRows = rawRows.filter((row: any) => {
        for (const [field, filterValue] of Object.entries(filters)) {
          const values = Array.isArray(filterValue) ? filterValue : [filterValue];
          for (const fv of values) {
            if (!matchesApiFilter(row[field], fv)) return false;
          }
        }
        return true;
      });
      log.info(`Filtered ${rawRows.length} rows to ${filteredRows.length} using filters`, { filters });
    }
    
    for (let i = 0; i < filteredRows.length; i++) {
      const row = filteredRows[i];
      
      // Try to find an ID field in the row data
      let id: string | number = `${fileId}-${i}`;
      if (idField && row[idField] !== undefined) {
        id = row[idField];
      } else {
        // Fallback: look for common ID field names
        for (const key of Object.keys(row)) {
          if (key === 'id' || key === 'ID' || key === '_id' || key.endsWith('_id')) {
            id = row[key];
            break;
          }
        }
      }
      
      allPoints.push({
        coords: [row.longitude, row.latitude],
        weight: 1,
        holes: [],
        id,
        // Include extra fields for color code joins
        ...(colorCodeFields && colorCodeFields.length > 0 && {
          fields: colorCodeFields.reduce((acc, field) => {
            if (row[field] !== undefined) acc[field] = row[field];
            return acc;
          }, {} as Record<string, any>)
        }),
      });
    }
    
    log.success('All points loaded', { count: allPoints.length });
    
    return allPoints;
  } catch (error) {
    // If endpoint not found, provide helpful error message
    if (error instanceof Error && error.message === 'ENDPOINT_NOT_FOUND') {
      throw new Error(
        'The /retrieve-all endpoint is not yet deployed. ' +
        'Please deploy the backend or use a dataset with >= 500 points to test viewport-based loading.'
      );
    }
    
    // If H3 cells not found in metadata
    if (error instanceof Error && error.message === 'H3_CELLS_NOT_FOUND') {
      throw new Error(
        'The backend needs to populate the h3_cells field in metadata. ' +
        'Please reprocess this file or use a dataset with >= 500 points to test viewport-based loading.'
      );
    }
    
    throw error;
  }
};

/**
 * Load points within viewport with optional filtering
 */
const loadPointsInViewport = async (
  fileId: string,
  viewport: Viewport,
  version: number,
  filterRes: number | null,
  filters?: Record<string, string | string[]>,
  colorCodeFields?: string[]
): Promise<DataPoint[]> => {
  const startTime = performance.now();
  
  if (!viewport.bounds) {
    log.error('Viewport bounds are required for point loading');
    return [];
  }
  
  // Get H3 cells that have data from metadata (for res-2 cell routing)
  const baseResolution = H3_CONFIG.BASE_RESOLUTION;
  const h3BaseCells = await getH3CellsForViewport(viewport, baseResolution);
  const cellsWithData = await getH3CellsWithData(fileId, version);
  
  // Filter to only cells that have data
  const cellsToQuery = cellsWithData.size > 0 
    ? h3BaseCells.filter(cell => cellsWithData.has(cell))
    : h3BaseCells;
  
  // Track for debugging
  lastRequestedH3Cells = cellsToQuery;
  
  log.info(`Found ${cellsToQuery.length} res-${baseResolution} cells with data in viewport`);
  
  // Fetch points using viewport bounds instead of H3 cell lists
  const fetchPromises = cellsToQuery.map(async (h3BaseCell) => {
    try {
      const url = `${API_BASE_URL}/retrieve`;
      const fieldsToFetch = ['latitude', 'longitude', ...(colorCodeFields || [])];
      const requestBody: any = {
        h3: h3BaseCell,
        fileId,
        version,
        fields: fieldsToFetch,
        viewportBounds: viewport.bounds,
        // For points mode, send filterRes for padding calculation
        // Backend will use this to pad the viewport appropriately
        ...(filterRes !== null && { aggRes: filterRes }),
      };
      
      // Pass filters to API if present
      if (filters && Object.keys(filters).length > 0) {
        requestBody.filters = filters;
      }
      
      log.info(`Fetching points from res-2 cell ${h3BaseCell} with viewport bounds`, {
        bounds: viewport.bounds,
        filterRes,
      });
      
      const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(requestBody),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        log.warn(`Failed to fetch res-2 cell ${h3BaseCell}: ${response.status}`, { error: errorText });
        return [];
      }
      
      const data = await response.json();
      
      log.info(`Received ${data.data?.length || 0} rows from API for cell ${h3BaseCell}`);
      
      const points = data.data.map((row: any, index: number) => {
        let id: string | number = `${h3BaseCell}-${index}`;
        for (const key of Object.keys(row)) {
          if (key === 'id' || key === 'ID' || key === '_id' || key.endsWith('_id')) {
            id = row[key];
            break;
          }
        }
        
        return {
          coords: [row.longitude, row.latitude] as [number, number],
          weight: 1,
          holes: [],
          id,
          // Include extra fields for color code joins
          ...(colorCodeFields && colorCodeFields.length > 0 && {
            fields: colorCodeFields.reduce((acc, field) => {
              if (row[field] !== undefined) acc[field] = row[field];
              return acc;
            }, {} as Record<string, any>)
          }),
        };
      });
      
      return points;
    } catch (error) {
      log.warn(`Failed to fetch cell ${h3BaseCell}`, error);
      return [];
    }
  });
  
  const results = await Promise.all(fetchPromises);
  const allPoints = results.flat();
  
  const duration = performance.now() - startTime;
  log.success(`Points loading completed in ${duration.toFixed(2)}ms`, {
    total: allPoints.length,
  });
  
  return allPoints;
};

/**
 * Load aggregations at specified H3 resolution with viewport bounds filtering
 */
const loadAggregations = async (
  fileId: string,
  viewport: Viewport,
  aggregationLevel: number,
  version: number,
  filterRes: number | null,
  filters?: Record<string, string | string[]>
): Promise<AggregatedDataPoint[]> => {
  const startTime = performance.now();

  if (!viewport.bounds) {
    log.error('Viewport bounds are required for aggregation loading');
    return [];
  }

  // Get all res-2 geohashes required to fill the viewport
  const baseResolution = H3_CONFIG.BASE_RESOLUTION;
  const h3BaseCells = await getH3CellsForViewport(viewport, baseResolution);

  // Get H3 cells that have data from metadata
  const cellsWithData = await getH3CellsWithData(fileId, version);

  // Filter to only cells that have data
  const cellsToQuery = cellsWithData.size > 0
    ? h3BaseCells.filter(cell => cellsWithData.has(cell))
    : h3BaseCells;

  // Track for debugging
  lastRequestedH3Cells = cellsToQuery;

  log.info(`Found ${cellsToQuery.length} res-${baseResolution} cells with data in viewport`);

  // Fetch aggregations using viewport bounds instead of cell lists
  const fetchPromises = cellsToQuery.map(async (h3BaseCell) => {
    try {
      const url = `${API_BASE_URL}/retrieve`;
      
      const requestBody: any = {
        h3: h3BaseCell,
        fileId,
        version,
        aggRes: aggregationLevel,
        viewportBounds: viewport.bounds,
      };

      // Pass filters to API if present
      if (filters && Object.keys(filters).length > 0) {
        requestBody.filters = filters;
      }
      
      log.info(`Fetching aggregations from res-2 cell ${h3BaseCell} with viewport bounds`, {
        bounds: viewport.bounds,
        aggRes: aggregationLevel,
      });
      
      const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(requestBody),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        log.warn(`Failed to fetch res-2 cell ${h3BaseCell}: ${response.status}`, { error: errorText });
        return [];
      }

      const data = await response.json();
      const aggregationColumnName = H3_CONFIG.getColumnName(aggregationLevel);

      // Convert response to AggregatedDataPoint format
      const aggregations: AggregatedDataPoint[] = [];
      for (const row of data.data) {
        const h3Cell = row[aggregationColumnName];

        if (h3Cell) {
          const recordCount = row.record_count || 1;
          const boundary = await getCellBoundary(h3Cell);

          aggregations.push({
            h3Index: h3Cell,
            recordCount,
            coords: boundary,
          });
        }
      }
      
      log.info(`Received ${aggregations.length} aggregations from cell ${h3BaseCell}`);
      
      return aggregations;
    } catch (error) {
      log.warn(`Failed to fetch cell ${h3BaseCell}`, error);
      return [];
    }
  });

  const results = await Promise.all(fetchPromises);
  const allAggregations = results.flat();

  const duration = performance.now() - startTime;
  log.success(`Aggregation loading completed in ${duration.toFixed(2)}ms`, {
    total: allAggregations.length,
  });

  return allAggregations;
};

/**
 * Get H3 cells that cover the viewport
 */
const getH3CellsForViewport = async (viewport: Viewport, resolution: number): Promise<string[]> => {
  // Use h3-js to get cells covering the viewport
  const { polygonToCells, polygonToCellsExperimental, latLngToCell } = await import('h3-js');
  
  if (!viewport.bounds) {
    // If no bounds, at least return the cell containing the center point
    const centerCell = latLngToCell(viewport.latitude, viewport.longitude, resolution);
    return [centerCell];
  }
  
  const { north, south, east, west } = viewport.bounds;
  
  // Create polygon from bounds (h3-js expects [lat, lng] format)
  const polygon: [number, number][] = [
    [north, west],
    [north, east],
    [south, east],
    [south, west],
    [north, west], // Close the polygon
  ];
  
  try {
    // Use experimental overlapping mode to include cells that intersect the viewport edges
    // This ensures corner cells are included even if their centroids are outside
    const cells = polygonToCellsExperimental(polygon, resolution, 'containmentOverlapping');
    
    log.info(`polygonToCellsExperimental returned ${cells.length} cells at res-${resolution} (overlapping mode)`);
    
    // If we got cells, return them
    if (cells.length > 0) {
      return cells;
    }
    
    // Fallback if experimental returns empty
    log.warn('polygonToCellsExperimental returned no cells, trying standard polygonToCells');
    const standardCells = polygonToCells(polygon, resolution);
    
    if (standardCells.length > 0) {
      return standardCells;
    }
    
    // If still no cells, fall through to corner cell logic
    log.warn('polygonToCells also returned no cells, using corner cells');
  } catch (error) {
    log.warn('polygonToCellsExperimental failed, using corner cells', error);
  }
  
  // Final fallback: get cells for corners + center and dedupe
  const cellsSet = new Set<string>();
  
  // Add cell for each corner
  cellsSet.add(latLngToCell(north, west, resolution));  // NW
  cellsSet.add(latLngToCell(north, east, resolution));  // NE
  cellsSet.add(latLngToCell(south, east, resolution));  // SE
  cellsSet.add(latLngToCell(south, west, resolution));  // SW
  
  // Add cell for center
  cellsSet.add(latLngToCell(viewport.latitude, viewport.longitude, resolution));
  
  // Add cells for midpoints of each edge to ensure better coverage
  cellsSet.add(latLngToCell(north, (west + east) / 2, resolution));  // North edge
  cellsSet.add(latLngToCell(south, (west + east) / 2, resolution));  // South edge
  cellsSet.add(latLngToCell((north + south) / 2, west, resolution)); // West edge
  cellsSet.add(latLngToCell((north + south) / 2, east, resolution)); // East edge
  
  log.info(`Using ${cellsSet.size} corner/edge cells as fallback`);
  
  return Array.from(cellsSet);
};

/**
 * Get cell boundary coordinates
 */
const getCellBoundary = async (h3Index: string): Promise<[number, number][]> => {
  const { cellToBoundary } = await import('h3-js');
  const boundary = cellToBoundary(h3Index);
  
  // Convert from [lat, lng] to [lng, lat] for deck.gl
  return boundary.map(([lat, lng]) => [lng, lat]);
};

/**
 * Fetch a single record by its ID
 * Used for displaying detailed information in overlays
 */
export const fetchRecordById = async (
  fileId: string,
  version: number,
  recordId: string | number,
  latitude: number,
  longitude: number,
  fields: string[],
  idField?: string
): Promise<any> => {
  try {
    // Calculate h3 index at resolution 2 from coordinates to know which parquet file to query
    const { latLngToCell } = await import('h3-js');
    const h3_res_2 = latLngToCell(latitude, longitude, 2);
    
    // Always include latitude/longitude for reference
    const allFields = new Set(fields);
    allFields.add('latitude');
    allFields.add('longitude');
    
    // Include the id field so we can verify the match
    if (idField) {
      allFields.add(idField);
    }
    
    const url = `${API_BASE_URL}/retrieve`;
    
    // Build filters to match by ID directly
    const filters: Record<string, string | number> = {};
    if (idField && recordId !== undefined && recordId !== null) {
      filters[idField] = String(recordId);
    }
    
    const requestBody: any = {
      h3: h3_res_2,
      fileId,
      version,
      fields: Array.from(allFields),
      filters,
      limit: 1,
    };
    
    // Only use viewport bounds as a fallback if no ID filter is available
    if (!idField || recordId === undefined || recordId === null) {
      requestBody.viewportBounds = {
        north: latitude + 0.0001,
        south: latitude - 0.0001,
        east: longitude + 0.0001,
        west: longitude - 0.0001,
      };
    }
    
    log.info('Fetching record by ID', {
      fileId,
      version,
      recordId,
      idField,
      latitude,
      longitude,
      h3_res_2,
      fields,
    });
    
    const response = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(requestBody),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch record: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (!data.data || data.data.length === 0) {
      log.warn('Record not found by ID', { recordId, idField, latitude, longitude });
      return null;
    }
    
    // If we filtered by ID, take the first (and should be only) result
    if (idField && recordId !== undefined && recordId !== null) {
      log.success('Record found by ID', { recordId, record: data.data[0] });
      return data.data[0];
    }
    
    // Fallback: find exact coordinate match (not proximity)
    for (const row of data.data) {
      if (row.latitude === latitude && row.longitude === longitude) {
        log.success('Record found by exact coordinates', { record: row });
        return row;
      }
    }
    
    // If no exact match, return first result but log a warning
    log.warn('No exact match found, returning first result', { recordId, latitude, longitude });
    return data.data[0];
  } catch (error) {
    log.error('Failed to fetch record', error);
    throw error;
  }
};
